import { memo, useState } from 'react';
import Link from 'next/link';
import { ButtonEl } from 'components/site/site_sections';
import Url from 'lib/siteurls';
import NextImage from 'next/image';
import { createGlobalStyle } from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';
import { SiteDot } from 'lib/svgs/site-dot';
import { buttonEventHandler } from 'lib/utils/button-event-handler';

const SustainabilityStyles = createGlobalStyle`
  .pds08-sustainability {
  max-width: none !important;
  padding: 0 !important;
  background-color: var(--fill-secondary);

  > .container {
    max-width: 1440px;
    padding: 30px;
    margin: 0px auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-wrap: wrap;

    @media ${mqLargeMin} {
      flex-direction: row;
      padding: 50px;
      margin: 50px auto 100px;
    }
  }
  .psd08-img {
    width: 100%;
    display: inline-block;
  }

  .text {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    @media ${mqLargeMin} {
      width: 25%;
      margin-right: 50px;
    }

    .button {
      display: none;
      i {
        transform: scale(0.8);
        padding: 0 20px;
      }

      @media ${mqLargeMin} {
        display: flex;
      }
    }
  }

  .text-top {
    h2 {
      span {
        display: block;
        font-size: 26px;
        font-weight: 300;
        color: var(--text-primary);
        line-height: 1.1;
      }

      @media ${mqLargeMin} {
        span {
          font-size: 30px;
        }
      }
    }

    p {
      color: var(--text-primary);
      margin: 30px 0;
    }
  }

  .media {
    display: flex;
    flex-direction: column;
    gap: 42px;
    margin-top: 60px;
    align-items: center;

    @media ${mqLargeMin} {
      margin: 0;
      gap: 50px;
      flex-direction: row;
      align-items: flex-start;
    }

    img {
      height: 300px;
      width: 300px;
      object-fit: cover;
    }
  }

  .media-item {
    width: 300px;

    h4,
    .h4-style {
      font-weight: 500;
      font-size: 16px;
      margin: 15px 0 13px;

      @media ${mqLargeMin} {
        margin-top: 37px;
      }
    }

    h4,
    .h4-style,
    p {
      color: var(--text-primary);
    }
  }

  span {
    color: var(--text-primary) !important;
  }
}

`;

const media = [
  {
    img: 'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649199896/PDS%20PDP/month1.png',
    title: 'Month 1 — Welcome Kit',
    desc: 'Biofilm Sachets (30-day supply) Aluminum BacTrac™ container',
    inMedia: true,
  },
  {
    img: 'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649199895/PDS%20PDP/month2.png',
    title: 'Month 2 —  Sustainable Refills',
    desc: 'Biofilm Sachets (30-day supply)',
    inMedia: true,
  },
  {
    img: 'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649263701/PDS%20PDP/pds_full_width_wk.png',
    title: 'PDS-08',
    desc: 'PDS-08® is a biodegradable, biocompatible, and bioremediable welcome kit',
    inMedia: false,
  },
];

type MediaItemProps = {
  data: any;
};

const MediaItem = ({ data }: MediaItemProps) => (
  <div className='media-item'>
    <NextImage src={data.img} alt={data.title || ''} width={300} height={300} />
    <h3 className='h4-style'> {data.title}</h3>
    <p>{data.desc}</p>
  </div>
);

type PDS08SustainabilityProps = {
  priceString: string;
};

const PDS08Sustainability = ({ priceString }: PDS08SustainabilityProps) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <section className='pds08-sustainability' id='sustainability'>
      <SustainabilityStyles />
      <div className='container'>
        <div className='psd08-img'>
          <img
            src={media[media.length - 1].img}
            alt='PDS-08'
            width='100%'
            height='auto'
            loading='lazy'
          />
        </div>

        <div className='text'>
          <div className='text-top'>
            <h2>
              <span>Sustainably</span>

              <span>delivered, monthly.</span>
            </h2>
            <p>
              Each component of our refill system is designed to protect
              PDS-08® and be gentler on our Earth.
            </p>

            <ButtonEl
              data={{
                fields: {
                  icon: 'plus_circle',
                  altIcon: 'minus_circle',
                  label: 'Learn More',
                  modal: 'modal-pds08-sustainability',
                },
              }}
            />
          </div>
          <Link href={Url.buy_pds08} prefetch={false} legacyBehavior>
            <button
              onClick={() =>
                buttonEventHandler('PDS subscribe now', 'PDP sustainability ')
              }
              className='button button-outline-accent'
              type='button'
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <span>Subscribe Now</span>
              <i>
                <SiteDot
                  width={12}
                  height={12}
                  color={isHovered ? 'var(--text-inverse-primary)' : 'var(--text-primary)'}
                />
              </i>
              <span>{priceString}</span>
            </button>
          </Link>
        </div>
        <div className='media'>
          {media
            .filter((v) => v.inMedia)
            .map((data, i) => (
              <MediaItem key={`media-item-${i}`} data={data} />
            ))}
        </div>
      </div>
    </section>
  );
};

export default memo(PDS08Sustainability);
